import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api";
import { toast } from "react-toastify";
import BattleScreen from "../Battle/BattleScreen";

const PvPBattleScreen = () => {
  const { battle_id } = useParams();
  const navigate = useNavigate();
  const [battleData, setBattleData] = useState(null);
  const [pvpUpdate, setPvpUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const wsConnectedRef = useRef(false); // Track if WebSocket is already connected

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      console.error("No JWT token found for WebSocket connection");
      return;
    }

    // Use the same host as the API but with ws:// or wss:// protocol
    const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    const wsProtocol = apiBase.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiBase.replace(/^https?:\/\//, '').replace(/\/v1$/, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/pvp-battle?token=${encodeURIComponent(token)}`;
    console.log("Connecting to WebSocket:", wsUrl);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("✅ PvP WebSocket connected successfully");
      // Send battle_id to join this specific battle room
      const message = { battle_id: battle_id };
      console.log("Sending join message:", message);
      ws.send(JSON.stringify(message));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("🔔 PvP WebSocket message received:", data);

        if (data.type === "opponent_chose_move") {
          // Opponent chose their move and is waiting for us - it's now our turn!
          console.log("⏳ Opponent is waiting for you to choose your move - it's your turn now!");
          
          // Update turn state via pvpUpdate
          if (data.current_turn) {
            const turnUpdate = {
              battle_id: battle_id,
              opponent_type: "pvp",
              current_turn: data.current_turn,
              _timestamp: Date.now() // Force new object reference
            };
            console.log("📊 Updating turn via pvpUpdate:", turnUpdate);
            setPvpUpdate(turnUpdate);
          }
          return;
        } else if (data.type === "pvp_action") {
          console.log("⚔️ Opponent action detected!");
          
          // Ignore waiting state messages
          if (data.result && data.result.waiting_for_opponent) {
            console.log("Opponent is waiting for you to choose");
            return;
          }
          
          // Handle switch actions (update immediately)
          if (data.action === "switch" && data.result) {
            console.log("Opponent switched Pokemon! Updating battle state...");
            const updatedData = {
              battle_id: battle_id,
              opponent_type: "pvp",
              player: data.result.player,
              foe: data.result.opponent,
              party: data.result.party,
              log: data.result.log,  // Include battle logs!
              current_turn: data.result.current_turn,
              turn_number: data.result.turn_number
            };
            console.log("📊 Triggering pvpUpdate for switch with logs:", data.result.log);
            setPvpUpdate(updatedData);
            return;
          }
          
          // Handle both_moves_executed
          if (data.result && data.result.both_moves_executed) {
            console.log("Both moves executed! Updating battle state...");
            const updatedData = {
              battle_id: battle_id,
              opponent_type: "pvp",
              player: data.result.player,
              foe: data.result.opponent,
              party: data.result.party,
              log: data.result.log,  // Include battle logs!
              current_turn: data.result.current_turn,
              turn_number: data.result.turn_number,
              both_moves_executed: true,
              first_player: data.result.first_player,
              second_player: data.result.second_player,
              first_move: data.result.first_move,
              second_move: data.result.second_move,
              battle_over: data.result.battle_over,
              winner: data.result.winner,
              coins_won: data.result.coins_won,
              coins_lost: data.result.coins_lost,
              currency_type: data.result.currency_type
            };
            console.log("📊 Triggering pvpUpdate for dual execution with logs:", data.result.log);
            console.log("🏆 Battle over status:", data.result.battle_over, "Winner:", data.result.winner);
            setPvpUpdate(updatedData);
          }
        } else if (data.type === "battle_end") {
          toast.info("Battle has ended!", {
            position: "top-center",
            autoClose: 3000
          });
        } else if (data.type === "connected") {
          console.log("✅ Connected to PvP battle:", data.battle_id);
        } else if (data.type === "opponent_connected") {
          console.log("👥 Opponent connected:", data.username);
          toast.info(`${data.username} has joined the battle!`, {
            position: "top-center",
            autoClose: 2000
          });
        }
      } catch (err) {
        console.error("Error parsing WebSocket message:", err);
      }
    };

    ws.onerror = (error) => {
      console.error("PvP WebSocket error:", error);
    };

    ws.onclose = () => {
      console.log("PvP WebSocket disconnected");
    };
  }, [battle_id, setPvpUpdate]); // Dependencies for useCallback

  useEffect(() => {
    const initializeBattle = async () => {
      try {
        // Initialize the PvP battle - this loads rosters and sets up battle state
        const response = await api.post(`/v1/pvp/battle/${battle_id}/start`, {}, { withCredentials: true });
        
        if (!response.data) {
          throw new Error("Failed to start battle");
        }

        // Transform PvP battle data to match BattleScreen format
        const battleState = response.data;
        const battleData = {
          battle_id: battle_id,
          opponent_type: "pvp",
          player: {
            id: battleState.player.id,
            name: battleState.player.name,
            level: battleState.player.level,
            current_hp: battleState.player.current_hp,
            stats: battleState.player.stats,
            moves: battleState.player.moves,
            nature: battleState.player.nature,
            slot: battleState.player.slot
          },
          foe: {
            id: battleState.opponent.id,
            name: battleState.opponent.name,
            level: battleState.opponent.level,
            current_hp: battleState.opponent.current_hp,
            stats: battleState.opponent.stats,
            moves: battleState.opponent.moves,
            nature: battleState.opponent.nature
          },
          party: battleState.party,
          current_turn: battleState.current_turn,
          turn_number: battleState.turn_number
        };

        setBattleData(battleData);
        setLoading(false);

      } catch (err) {
        console.error("Failed to initialize PvP battle:", err);
        const errorMsg = err.response?.data?.detail || err.message || "Failed to load battle";
        setError(errorMsg);
        toast.error(errorMsg, {
          position: "top-center",
          autoClose: 3000
        });
        
        // Redirect back to manage battles after error
        setTimeout(() => {
          navigate("/manage-battles");
        }, 3000);
      }
    };

    initializeBattle();
  }, [battle_id, navigate]); // Added navigate back to fix lint warning

  // Separate effect for WebSocket connection - only connect once
  useEffect(() => {
    // Only connect WebSocket after battle data is loaded and if not already connected
    if (battleData && !wsConnectedRef.current) {
      console.log("Connecting WebSocket for the first time");
      connectWebSocket();
      wsConnectedRef.current = true;
    }
    // No cleanup here - we don't want to close on every render
  }, [battleData, connectWebSocket]); // battleData triggers initial connection, but ref prevents reconnections

  // Separate effect for cleanup on unmount only
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        console.log("Cleaning up WebSocket connection on component unmount");
        wsRef.current.close();
        wsConnectedRef.current = false;
      }
    };
  }, []); // Empty deps = only runs on mount/unmount

  const handleBattleEnd = (result) => {
    console.log("PvP Battle ended:", result);
    
    // Show result message
    if (result.winner === "player") {
      toast.success("🏆 Victory! You won the battle!", {
        position: "top-center",
        autoClose: 3000
      });
    } else {
      toast.error("💀 Defeat! You lost the battle.", {
        position: "top-center",
        autoClose: 3000
      });
    }

    // Redirect to manage battles
    setTimeout(() => {
      navigate("/manage-battles");
    }, 3000);
  };

  const handleExit = () => {
    navigate("/manage-battles");
  };

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#1a1d2e",
        color: "#fff"
      }}>
        <div style={{ textAlign: "center" }}>
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="sr-only">Loading...</span>
          </div>
          <h4>Loading PvP Battle...</h4>
          <p className="text-muted">Preparing battle arena</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#1a1d2e",
        color: "#fff"
      }}>
        <div style={{ textAlign: "center", maxWidth: "500px", padding: "2rem" }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: "64px", color: "#dc3545" }}></i>
          <h3 className="mt-3">Battle Error</h3>
          <p className="text-muted">{error}</p>
          <button 
            className="btn btn-primary mt-3"
            onClick={() => navigate("/manage-battles")}
          >
            Return to Manage Battles
          </button>
        </div>
      </div>
    );
  }

  // Render BattleScreen with PvP data
  if (battleData && battleData.player && battleData.foe) {
    return (
      <BattleScreen
        initialData={battleData}
        onExit={handleExit}
        onBattleEnd={handleBattleEnd}
        pvpUpdate={pvpUpdate}
      />
    );
  }

  // Fallback - should not reach here if data loaded successfully
  return null;
};

export default PvPBattleScreen;
